import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { validatePassword } from "../../../common/utils/validation";
import { resetPassword } from "../../../services/authService";
import logger from "../../../common/utils/logger";
import "./ResetPassword.css";

type ResetView = "form" | "success" | "invalid";

type ResetErrors = {
  password: string;
  confirm: string;
};

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";

  const [view, setView] = useState<ResetView>("form");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<ResetErrors>({ password: "", confirm: "" });
  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!token) setView("invalid");
  }, [token]);

  const getConfirmError = (nextPassword: string, nextConfirmPassword: string) => {
    if (!nextConfirmPassword) return "";
    return nextPassword !== nextConfirmPassword ? "Passwords do not match." : "";
  };

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const pwErr = validatePassword(password) ?? "";
      const confirmErr = password !== confirmPassword ? "Passwords do not match." : "";

      setErrors({ password: pwErr, confirm: confirmErr });
      if (pwErr || confirmErr) return;

      setIsLoading(true);
      setApiError("");

      try {
        await resetPassword(token, password);
        setView("success");
      } catch (err) {
        logger.error("Reset password error:", err);
        const msg =
          err instanceof Error ? err.message : "Something went wrong. Please try again.";
        if (/expired|invalid/i.test(msg)) {
          setView("invalid");
        } else {
          setApiError(msg);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [token, password, confirmPassword]
  );

  const EyeIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  return (
    <div className="reset-overlay">
      <div className="reset-modal">
        <div className="reset-header">
          <h2 className="reset-title">SeatSync</h2>
          <p className="reset-subtitle">powered by TAM</p>
        </div>

        {view === "form" && (
          <form className="reset-form" onSubmit={handleSubmit}>
            <h3 className="reset-form-heading">Set a new password</h3>
            <p className="reset-form-desc">
              Must be 8–12 characters with at least one uppercase letter, one lowercase letter,
              and one special character.
            </p>

            <div className="reset-input-group">
              <label className="reset-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New password"
                  value={password}
                  onChange={(e) => {
                    const nextPassword = e.target.value;
                    setPassword(nextPassword);
                    setErrors((prev) => ({
                      ...prev,
                      password: prev.password ? validatePassword(nextPassword) ?? "" : "",
                      confirm: getConfirmError(nextPassword, confirmPassword),
                    }));
                  }}
                  className="reset-input"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="reset-eye-btn"
                  onMouseDown={() => setShowPassword(true)}
                  onMouseUp={() => setShowPassword(false)}
                  onMouseLeave={() => setShowPassword(false)}
                  onTouchStart={() => setShowPassword(true)}
                  onTouchEnd={() => setShowPassword(false)}
                  disabled={isLoading}
                  aria-label="Hold to view password"
                >
                  <EyeIcon />
                </button>
              </label>
              {errors.password && <p className="reset-error">{errors.password}</p>}
            </div>

            <div className="reset-input-group">
              <label className="reset-input-wrapper">
                <input
                  type={showConfirm ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    const nextConfirmPassword = e.target.value;
                    setConfirmPassword(nextConfirmPassword);
                    setErrors((prev) => ({
                      ...prev,
                      confirm: getConfirmError(password, nextConfirmPassword),
                    }));
                  }}
                  className="reset-input"
                  disabled={isLoading}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="reset-eye-btn"
                  onMouseDown={() => setShowConfirm(true)}
                  onMouseUp={() => setShowConfirm(false)}
                  onMouseLeave={() => setShowConfirm(false)}
                  onTouchStart={() => setShowConfirm(true)}
                  onTouchEnd={() => setShowConfirm(false)}
                  disabled={isLoading}
                  aria-label="Hold to view confirm password"
                >
                  <EyeIcon />
                </button>
              </label>
              {errors.confirm && <p className="reset-error">{errors.confirm}</p>}
            </div>

            {apiError && <p className="reset-error reset-error--api">{apiError}</p>}

            <button type="submit" className="reset-submit-btn" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner-small"></span>
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </button>
          </form>
        )}

        {view === "success" && (
          <div className="reset-state">
            <div className="reset-state-icon reset-state-icon--success" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path strokeLinecap="round" strokeLinejoin="round" d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <h3 className="reset-state-title">Password updated!</h3>
            <p className="reset-state-msg">
              Your password has been reset. You can now log in with your new password.
            </p>
            <button type="button" className="reset-submit-btn" onClick={() => navigate("/")}>
              Go to Login
            </button>
          </div>
        )}

        {view === "invalid" && (
          <div className="reset-state">
            <div className="reset-state-icon reset-state-icon--error" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path strokeLinecap="round" strokeLinejoin="round" d="m15 9-6 6M9 9l6 6" />
              </svg>
            </div>
            <h3 className="reset-state-title">Link expired or invalid</h3>
            <p className="reset-state-msg">
              This password reset link is invalid or has expired. Please request a new one from
              the login page.
            </p>
            <button type="button" className="reset-submit-btn" onClick={() => navigate("/")}>
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
